import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  MapPin,
  Ruler,
  Wallet,
  CalendarRange,
  ImageOff,
  Tag,
  PlusCircle,
} from 'lucide-react';
import { getListingsByProviderId } from '../../services/adminproviders.api';
import { Listing } from '../../types/pppadminproviders-model';

const formatDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const ListingCard: React.FC<{ listing: Listing }> = ({ listing }) => {
  const cover = listing.photos?.find(p => p && p !== 'string');

  const extraCharges = [
    { label: 'Wall Painting', value: listing.additionalCharges?.wallPaintingCharge },
    { label: 'Installation', value: listing.additionalCharges?.installationCharge },
    { label: 'Removal', value: listing.additionalCharges?.removalCharge },
    { label: 'Other', value: listing.additionalCharges?.otherCharge },
  ].filter(c => typeof c.value === 'number' && c.value > 0);

  return (
    <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm transition hover:shadow-md">
      <div className="flex h-40 w-full items-center justify-center overflow-hidden bg-stone-100">
        {cover ? (
          <img src={cover} alt={listing.title} className="h-full w-full object-cover" />
        ) : (
          <ImageOff size={22} className="text-stone-300" />
        )}
      </div>

      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="truncate text-[14.5px] font-bold text-stone-800">{listing.title}</h3>
          <span className="flex-shrink-0 rounded-full bg-amber-50 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-wide text-amber-700 ring-1 ring-inset ring-amber-200">
            {listing.mediaType.replace(/_/g, ' ')}
          </span>
        </div>

        <div className="flex items-start gap-2 text-[12.5px] text-stone-500">
          <MapPin size={14} className="mt-0.5 flex-shrink-0 text-stone-400" />
          <span className="line-clamp-2">
            {[listing.landmark, listing.area, listing.city, listing.state, listing.country]
              .filter(Boolean)
              .join(', ')}
            {listing.pinCode ? ` - ${listing.pinCode}` : ''}
          </span>
        </div>

        <div className="flex items-center gap-2 text-[12.5px] text-stone-500">
          <Ruler size={14} className="flex-shrink-0 text-stone-400" />
          <span>
            {listing.width} ft × {listing.height} ft
          </span>
        </div>

        <div className="flex items-center gap-2 text-[12.5px] text-stone-500">
          <CalendarRange size={14} className="flex-shrink-0 text-stone-400" />
          <span>
            {formatDate(listing.availableFrom)} – {formatDate(listing.availableTo)}
          </span>
        </div>

        {listing.description && (
          <p className="line-clamp-2 text-[12.5px] leading-relaxed text-stone-500">{listing.description}</p>
        )}

        <div className="mt-1 flex items-center justify-between rounded-xl border border-stone-100 bg-stone-50/70 px-3 py-2.5">
          <div className="flex items-center gap-1.5 text-stone-700">
            <Wallet size={14} className="text-amber-600" />
            <span className="text-[13.5px] font-bold">{formatCurrency(listing.expectedRent)}</span>
            <span className="text-[11px] font-medium text-stone-400">/ period</span>
          </div>
        </div>

        {extraCharges.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {extraCharges.map(c => (
              <span
                key={c.label}
                className="flex items-center gap-1 rounded-full bg-stone-100 px-2 py-1 text-[10.5px] font-medium text-stone-500"
              >
                <Tag size={10} /> {c.label}: {formatCurrency(c.value as number)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ViewListings: React.FC = () => {
  const { id: providerId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!providerId) return;
    setLoading(true);
    setError('');
    getListingsByProviderId(providerId)
      .then(setListings)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [providerId]);

  if (!providerId) return null;

  return (
    <div className="min-h-screen bg-stone-50 px-8 py-7">
      <div className="mb-5 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-[13px] font-medium text-stone-500 transition hover:text-stone-800"
        >
          <ArrowLeft size={15} /> Back to Provider
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
        <div className="flex items-center justify-between bg-gradient-to-r from-amber-600 to-amber-500 px-8 py-6">
          <div>
            <h1 className="text-[18px] font-bold text-white">Provider Listings</h1>
            <p className="mt-0.5 text-[12.5px] text-amber-50/90">
              {loading ? 'Loading…' : `${listings.length} listing${listings.length === 1 ? '' : 's'} found`}
            </p>
          </div>
        </div>

        <div className="px-8 py-7">
          {loading && (
            <div className="flex items-center justify-center gap-3 py-16">
              <Loader2 size={20} className="animate-spin text-amber-600" />
              <span className="text-[13px] font-medium text-stone-500">Loading listings…</span>
            </div>
          )}

          {!loading && error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-600">
              {error}
            </div>
          )}

          {!loading && !error && listings.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <PlusCircle size={28} className="text-stone-300" />
              <p className="text-[13.5px] font-medium text-stone-500">
                This provider hasn't created any listings yet.
              </p>
            </div>
          )}

          {!loading && !error && listings.length > 0 && (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {listings.map(listing => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewListings;